import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent bg-clip-padding text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border-primary/20 bg-primary text-primary-foreground hover:bg-primary/[0.92]",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        outline: "border-input bg-background text-foreground hover:border-border hover:bg-muted/70",
        secondary: "border-border bg-secondary/80 text-secondary-foreground hover:bg-secondary",
        ghost: "hover:bg-muted/60 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        compact: "h-7 px-2.5 !text-[12px] [&_svg]:!size-3.5",
        compactIcon: "size-8 px-0 !text-[12px] [&_svg]:!size-4 sm:h-7 sm:w-auto sm:px-2.5 sm:[&_svg]:!size-3.5",
        sm: "h-8 px-3 text-sm",
        default: "h-9 px-3.5 py-2 text-sm",
        lg: "h-10 px-4 text-sm",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
